import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LocalzLogDetailsModal from 'react/containers/modals/LocalzLogDetailsModal';
import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  SelectField,
  TextField,
  DateField,
  LookupField
} from 'react/components/fields';

import { getResourceTo } from 'react/utils/utils';
import { actionCreators as localzLogActions } from 'react/api/actions/localzIntegrationLogs';
import { actionCreators as modalsActions } from 'react/api/actions/modals';
import { useGRID } from 'react/hooks/useGrid';

function LocalzIntegrationLogCrud(props) {
  const {
    getDateRangeFilters,
    getActualPage,
    setSortByClient,
    setCustomCustomizeColumns,
    setCustomFilterBuilderFields,
    setCustomOnClickRow,
    setCustomColumnsFormatter,
    setCustomSaveList,
    addDataToGrid
  } = useGRID('LocalzIntegrationLog');
  const tenant = Greenmile.Utils?.getTenant() ?? 'lts';

  const dispatch = useDispatch();
  const localzIntegrationLogs = useSelector((state) => state.localzIntegrationLogs.get('data', []));

  const nameSpace = useMemo(() => 'LocalzIntegrationLog', []);

  const hasAValidFilterValue = (value) =>
    (typeof(value) === 'string' && String(value).length) ||
    (typeof(value) === 'number' && value >= 0);

  const getActiveFilters = () => {
    const fields = App.Forms[nameSpace]?.filterBuilder?.fields || {};
    const activeFilters = {};

    Object.keys(fields).forEach((key) => {
      const filter = fields[key];

      switch (filter.name) {
        case 'requestDate_Range': {
          const customFormatter = (date) => new Date(date).toISOString();

          const { startDate, endDate } = getDateRangeFilters(filter, customFormatter);

          Object.assign(activeFilters, {
            startDate,
            endDate
          });

          break;
        }
        case 'organization': {
          const { model: { key: organizationKey } } = filter.getFullValue();

          if (organizationKey) {
            activeFilters.organizationKey = organizationKey;
          }

          break;
        }
        default: {
          const filterValue = filter.getValue();

          if (hasAValidFilterValue(filterValue)) {
            activeFilters[key] = filterValue;
          }

          break;
        }
      }
    });

    return activeFilters;
  };

  const getSafeMessageValue = ({
    message,
    status,
    errorMessage
  }) => {
    if (status === 200) return '-';

    if (errorMessage) return errorMessage;

    return message;
  }

  const getCustomColumnsFormatters = () => ({
    status: {
      width: 68,
      formatter: (value) => {
        const hasSucessStatus = String(value).charAt(0) === '2';
        return `<div class='httpStatus-${hasSucessStatus ? 'success' : 'error'}'></div>`;
      }
    },
    'organization.description': {
      formatter: (_, dataContext) => dataContext.organizationKey
    },
    'message': {
      formatter: (value, dataContext) => {
        if (!value || value === 'null') return '';

        try {
          const { status } = dataContext;
          const { message: errorMessage } = JSON.parse(value);

          return getSafeMessageValue({
            message: value,
            errorMessage,
            status
          });
        } catch (error) {
          return value;
        }
      }
    },
  });

  const overrideSaveList = () => {
    const fieldsToOverride = {
      'organization.description': 'organization'
    };

    const customFormatters = {
      organization: (_, data) => data.organizationKey,
      message: (_, data) => {
        const { status, message } = data;

        if (!message || message === 'null') return '';

        const { message: errorMessage } = JSON.parse(message);

        return getSafeMessageValue({
          message,
          errorMessage,
          status
        });
      }
    };

    setCustomSaveList('LocalzLogs', fieldsToOverride, customFormatters);
  };

  const setCustomExecuteFilter = () => {
    App.Forms[nameSpace].filterBuilder.customExecuteFilter = () => {
      Monet.Grid.Utils.hideGridNoResult(nameSpace);

      const page = getActualPage();

      if (page === 0)
        App.Forms[nameSpace].pagination.blocked = false;

      const activeFilters = getActiveFilters();

      const filtersPayload = {
        page: Math.ceil(page),
        pageSize: 51
      };

      Object.assign(filtersPayload, activeFilters);

      dispatch(localzLogActions.fetchLogs({ filtersPayload, tenant }));
    };
  };

  const customOnclick = (attributes) => {
     const {
      routeDate,
      requestDate,
      status,
      message,
      actionPerformed,
      organizationKey,
      routeKey,
      stopKey
    } = attributes;

    dispatch(modalsActions.openModal({
      modalKey: 'localzLogDetails',
      title: 'IntegrationLogModal.Title',
      data: {
        routeDate: Monet.Format.Date.formatToLocale(routeDate),
        requestDate: Monet.Format.DateTime.formatToLocale(requestDate),
        status,
        message,
        actionPerformed,
        organizationKey,
        routeKey,
        stopKey
      },
    }));
  };

  const beforeList = () => {
    overrideSaveList();
    setCustomCustomizeColumns();
    setSortByClient();
    setCustomFilterBuilderFields({
      fieldsToRemove: ['status', 'message', 'requestDate', 'routeDate_Range']
    });
    setCustomOnClickRow(customOnclick)
    setCustomColumnsFormatter(getCustomColumnsFormatters());
    setCustomExecuteFilter();
  };

  useEffect(() => {
   if (localzIntegrationLogs?.size >= 0) {
     const data = localzIntegrationLogs.toJS();

     addDataToGrid(data);
   }
  }, [localzIntegrationLogs]);

  return (
    <>
      <BaseCrud
        isGrid
        entity="LocalzIntegrationLog"
        customOptions={{
          columnCheckBox: {
            show: false,
          },
          rowHeight: 32,
          sanitizer: null
        }}
        gridCallbacks={{
          beforeList
        }}
        updateTitle={props.updateTitle}
      >
        <FormSection title={getResourceTo('EventLog')} className="generalSection">
          <FormSectionColumn>
            <TextField
              label={getResourceTo('wglnIntegration.field.status')}
              name="status"
            />
            <TextField
              label={getResourceTo('wglnIntegration.field.routeKey')}
              name="routeKey"
            />
            <TextField
              label={getResourceTo('stop.key')}
              name="stopKey"
            />
            <LookupField
              label={getResourceTo('wglnIntegration.field.organization')}
              name="organization"
              lookupEntity="Organization"
              lookupEntityColumn="description"
            />
            <DateField
              label={getResourceTo('wglnIntegration.field.routeDate')}
              name="routeDate"
              type="DATE"
            />
            <SelectField
              label={getResourceTo('wglnIntegration.field.actionPerformed')}
              name="actionPerformed"
              options={[
                {
                  label: 'DEPART_ORIGIN',
                  value: 'DEPART_ORIGIN'
                },
                {
                  label: 'COMPLETE_ROUTE',
                  value: 'COMPLETE_ROUTE'
                },
                {
                  label: 'CANCEL_STOP',
                  value: 'CANCEL_STOP'
                },
                {
                  label: 'REDELIVERY_STOP',
                  value: 'REDELIVERY_STOP'
                },
                {
                  label: 'START_ROUTE',
                  value: 'START_ROUTE'
                },
                {
                  label: 'DEPART_STOP',
                  value: 'DEPART_STOP'
                },
                {
                  label: 'NEXT_STOP',
                  value: 'NEXT_STOP'
                },
                {
                  label: 'UNDELIVERED_STOP',
                  value: 'UNDELIVERED_STOP'
                },
                {
                  label: 'ARRIVE_STOP',
                  value: 'ARRIVE_STOP'
                }
              ]}
            />
            <DateField
              label={getResourceTo('wglnIntegration.field.actionTime')}
              name="requestDate"
              type="DATE_TIME"
            />
            <TextField
              label={getResourceTo('wglnIntegration.field.message')}
              name="message"
            />
          </FormSectionColumn>
        </FormSection>
      </BaseCrud>
      <LocalzLogDetailsModal />
    </>
  );
}

LocalzIntegrationLogCrud.propTypes = {
  updateTitle: PropTypes.func.isRequired
};

export default LocalzIntegrationLogCrud;
